import {useContext, useMemo} from "react";

import {useQuery} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";

import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {UserProfileContext} from "./user-profile-provider.tsx";
import {profileFromQueryData, userProfileQuery} from "./user-profile-query.ts";

type ProfileResult =
    | {
          status: "aborted";
          profile: null;
      }
    | {
          status: "loading";
          profile: null;
      }
    | {
          status: "error";
          error: Error;
          profile: null;
      }
    | {
          status: "success";
          profile: ProfileModelType | null | undefined;
      };

/**
 * A hook that returns the full user profile for the logged-in user. If a
 * UserProfileContext is present, it returns data from that first; otherwise,
 * it will fetch the profile data from GraphQL.
 * TODO (FEI-4389): This currently doesn't share a cache with the existing APIs;
 * we need to update UserProfileProvider to use this hook internally instead!
 */
const useProfile = (): ProfileResult => {
    const profileContext = useContext(UserProfileContext);
    const resultFromContext = useMemo((): ProfileResult | null | undefined => {
        if (profileContext === null) {
            return null;
        }
        const {loading, error, profile} = profileContext;
        if (loading) {
            return {status: "loading", profile: null};
        }
        if (error) {
            return {status: "error", error, profile: null};
        }
        if (profile == null) {
            return {
                status: "error",
                error: new KAError(
                    "Couldn't retrieve user profile",
                    Errors.Internal,
                ),
                profile: null,
            };
        }
        return {status: "success", profile};
    }, [profileContext]);

    // Skip the query if we're using the result from context, or if there is no
    // logged-in profile to fetch.
    const skip = profileContext != null || !PermissionsInfo.hasProfile;
    const [queryResult] = useQuery(userProfileQuery, {skip});
    const resultFromQuery = useMemo((): ProfileResult => {
        if (queryResult.status === "success") {
            try {
                const profile = profileFromQueryData(queryResult.data);
                return {status: "success", profile};
            } catch (err: any) {
                return {status: "error", error: err, profile: null};
            }
        }
        if (queryResult.status === "no-data") {
            return {status: "success", profile: null};
        }

        return {profile: null, ...queryResult};
    }, [queryResult]);

    // Always return the result from context if it exists
    return resultFromContext ?? resultFromQuery;
};

export default useProfile;
